// meetingInfo.js -- builds the three-line "copy meeting info" string
// for a lead or customer record. Used by the Copy Meeting Info button
// on LeadDetail and CustomerDetail.
//
// Output shape (multi-line, requested by Dave 2026-06-30):
//   Site #ESS324036  •  Bulloch Esso  •  DUGALD, MB
//   Contact: Nashan Kang -- 431-373-1147
//   Host: David Cook, Managing Director -- 905.346.0864 ext 102
//
// Pastes cleanly into Teams/Zoom meeting notes, agenda fields, or a
// phone bridge invite. Falls back to "—" for any field that's blank,
// and drops the whole line when there's no useful content (e.g. no
// contact name AND no phone).

// Per-user dial-in strings. Keyed by email so it's robust to name
// changes / rebrands. Default falls back to the main office line.
const HOST_PHONES = {
  'david@xsitegroup.ca': '905.346.0864 ext 102',
  'mary@xsitegroup.ca':  '905.346.0864 ext 101',
  'zoe@xsitegroup.ca':   '905.346.0864',
  'kaden@xsitegroup.ca': '905.346.0864',
};
const DEFAULT_HOST_PHONE = '905.346.0864';

function hostPhoneFor(email) {
  if (!email) return DEFAULT_HOST_PHONE;
  return HOST_PHONES[String(email).toLowerCase()] || DEFAULT_HOST_PHONE;
}

function fullName(rec) {
  const parts = [rec.first_name, rec.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(' ').trim();
  // Fall back to company when no contact name is on the record.
  return rec.company || '';
}

function locationString(rec) {
  return [rec.city, rec.province_state].filter(Boolean).join(', ');
}

// Prefer cell over main when both exist; reps will call the cell first.
function contactPhone(rec) {
  return rec.phone_cell || rec.phone_main || '';
}

// Compose the three lines. Returns a single string with \n separators.
export function buildMeetingInfo(record, user) {
  if (!record) return '';
  const siteNum  = record.site_number ? `Site #${record.site_number}` : null;
  const posType  = record.pos_type || null;
  const loc      = locationString(record) || null;
  const headerBits = [siteNum, posType, loc].filter(Boolean).join('  •  ');

  const contact = fullName(record);
  const cphone  = contactPhone(record);
  const contactLine = (contact || cphone)
    ? `Contact: ${[contact, cphone].filter(Boolean).join(' -- ')}`
    : null;

  // Host = currently logged-in user. Title falls back to role.
  const hostName  = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || '';
  const hostTitle = user?.title || (user?.role === 'admin' ? 'Managing Director' : (user?.role || ''));
  const hostPhone = hostPhoneFor(user?.email);
  const hostBits  = [hostName, hostTitle].filter(Boolean).join(', ');
  const hostLine  = hostName
    ? `Host: ${hostBits} -- ${hostPhone}`
    : null;

  return [headerBits, contactLine, hostLine].filter(Boolean).join('\n');
}
